/* eslint-disable react/jsx-no-bind */
//@flow
import * as React from 'react';
import * as Immutable from 'immutable';
import { Map } from 'immutable';
import { type TFunction } from 'react-i18next';
import type { List } from 'immutable';
import {
    Text,
    Box,
    Columns,
    Column,
    Tooltip,
    Button
} from '@locus-taxy/neo-ui';
import OverlayDialog from 'common/components/core/dialog/OverlayDialog';
import EditPjpMetricContainer from 'common/entity/pjpMaster/EditPjpMetricContainer';
import { Loader } from 'common/components/core/loader/Loader';
import PjpMetricGraphs from './PjpMetricGraphs';
import type { PjpInsightsMetadata } from 'common/models/internal/PjpInsightsMetadata';
import type {
    PjpInsightsDashboard,
    PjpInsightsDashboardData
} from 'common/models/internal/PjpInsightsDashboard';
import type { PjpMetricGraph } from 'common/models/internal/PjpMetricGraph';
import { type ThemeType } from 'react-css-themr';
import {
    personnelClientIdSelector,
    personnelIdSelector
} from 'common/redux/selectors/sessionSelector';

import PjpInsightsActions from 'common/redux/actions/pjpInsights';

import {
    getPjpMetricsMetadata,
    isPjpDashboardsLoading,
    getAllPjpInsightsDashboards,
    isPjpMetricMetadataLoading
} from 'common/redux/selectors/pjpInsightsSelectors';

import { connect } from 'react-redux';

import {
    PermissionMatch,
    AccessDeniedMessage,
    RoleOperationEnum
} from 'common/modules/rbac/';

type Props = {
    selectedBU: string,
    setChartRef: React.RefObject,
    planId: string,
    clientId: string,
    theme: ThemeType,
    t: TFunction,
    areDashboardsLoading: boolean,
    isMetricsMetadataLoading: boolean,
    metricsMetadata: List<PjpInsightsMetadata>,
    dashboard: {
        dashboardData: PjpInsightsDashboardData
    },
    allDashboards: Array<PjpInsightsDashboard>,
    fetchAllDashboards: (clientId: string) => Promise<*>,
    fetchPjpMetricsMetadata: (clientId: string) => Promise<*>,
    fetchPjpMetric: (
        clientId: string,
        planId: string,
        metricId: string,
        groupBy: Array<string>,
        aggregate: string,
        graphId: string,
        includeExistingBeat: boolean,
        addMetricActive: boolean,
        flushExisting: boolean
    ) => void,
    clearConfiguredMetric: () => void,
    deleteDashboard: (clientId: string, dashboardId: string) => Promise<*>,
    personnelId: string,
    personnelClientId: string,
    dashboardGraphMap: Map<string, string>
};

type State = {
    addMetricActive: boolean,
    savedLayout: ?Object,
    existingPlanGraphs: Map<string, boolean>,
    metricsMetadata: ?List<PjpInsightsMetadata>,
    selectedMetricGraph: ?PjpMetricGraph
};

class PjpChartsContainer extends React.PureComponent<Props, State> {
    constructor(props) {
        super(props);
        this.state = {
            addMetricActive: false,
            savedLayout: null,
            existingPlanGraphs: new Map(),
            metricsMetadata: null,
            selectedMetricGraph: null
        };
    }

    componentDidMount() {
        this.fetchPjpChartsData();
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (
            nextProps.metricsMetadata &&
            this.props.metricsMetadata !== nextProps.metricsMetadata
        ) {
            this.setState({
                metricsMetadata: this.processMetricMetadata(
                    nextProps.metricsMetadata
                )
            });
        }
    }

    fetchPjpChartsData = () => {
        const {
            clientId,
            fetchAllDashboards,
            fetchPjpMetricsMetadata
        } = this.props;

        fetchAllDashboards(clientId).then(() => {
            this.getSelectedMetricGraphs();
        });
        fetchPjpMetricsMetadata(clientId);
    };

    fetchPjpMetric = (
        metricId,
        aggregate,
        groupBy,
        graphId,
        includeExistingBeat,
        addMetricActive,
        flushExisting
    ) => {
        const { clientId, fetchPjpMetric, planId } = this.props;
        fetchPjpMetric(
            clientId,
            planId,
            metricId,
            groupBy,
            aggregate,
            graphId,
            includeExistingBeat,
            addMetricActive,
            flushExisting
        );
    };

    processMetricMetadata = metadata => {
        const { t } = this.props;
        if (metadata) {
            const updatedMetadata = [];
            metadata.keySeq().forEach(metricKey => {
                updatedMetadata.push(
                    metadata
                        .get(metricKey)
                        .setLabel(t(`pjpAnalytics.${metricKey}`))
                );
            });
            return Immutable.List(updatedMetadata);
        }
        return metadata;
    };

    getSelectedMetricGraphs = () => {
        const { allDashboards } = this.props;
        let selectedGraphsMap = Map({});
        allDashboards.forEach(dashboard => {
            const metricGraph = dashboard.dashboardData.graphs.get(0);
            const { includeExistingBeat, id } = metricGraph;
            if (includeExistingBeat) {
                selectedGraphsMap = selectedGraphsMap.set(id, true);
            }
        });
        this.setState({
            existingPlanGraphs: selectedGraphsMap
        });
    };

    fetchLayout(props: Props) {
        const { personnelId, personnelClientId, planId } = props;
        if (!personnelId || !personnelClientId) {
            return;
        }
        const storedLayoutJson = localStorage.getItem(
            `${personnelClientId}/${personnelId}/analytics/${planId}`
        );
        if (!storedLayoutJson) {
            this.setState({ savedLayout: null });
            return;
        }
    }

    handleLayoutChange = (currentLayout, allLayouts) => {
        const {
            personnelId,
            personnelClientId,
            planId,
            dashboard
        } = this.props;
        if (dashboard && personnelId && personnelClientId) {
            localStorage.setItem(
                `${personnelClientId}/${personnelId}/analytics/${planId}`,
                JSON.stringify(allLayouts)
            );
            this.setState({ savedLayout: allLayouts });
        }
    };

    onEditChart = (graphId: string) => {
        const { allDashboards, dashboardGraphMap } = this.props;

        const selectedDashboardId = dashboardGraphMap.get(graphId);
        const selectedDashboard = allDashboards.find(
            dashboard => dashboard.dashboardId === selectedDashboardId
        );
        const selectedMetricGraph = selectedDashboard.dashboardData.graphs.get(
            0
        );
        this.setState({
            selectedMetricGraph,
            addMetricActive: true
        });
    };

    onEscapeClick = () => {
        const { clearConfiguredMetric } = this.props;
        this.setState(
            {
                addMetricActive: false,
                selectedMetricGraph: null
            },
            () => {
                clearConfiguredMetric();
            }
        );
    };

    onDeleteChart = (graphId: string) => {
        const { deleteDashboard, clientId, dashboardGraphMap } = this.props;
        const selectedDashboardId = dashboardGraphMap.get(graphId);
        deleteDashboard(clientId, selectedDashboardId).then(() => {
            this.fetchPjpChartsData();
        });
    };

    addMetric = () => {
        this.setState({
            addMetricActive: true
        });
    };

    pollFetchMetric = () => {
        return;
    };

    toggleExistingBeat = graphId => {
        const { existingPlanGraphs } = this.state;
        let updatedExistigBeatGraphs;
        if (existingPlanGraphs.has(graphId)) {
            updatedExistigBeatGraphs = existingPlanGraphs.delete(graphId);
        } else {
            updatedExistigBeatGraphs = existingPlanGraphs.set(graphId, true);
        }
        this.setState({
            existingPlanGraphs: updatedExistigBeatGraphs
        });
    };

    render() {
        const {
            theme,
            planId,
            areDashboardsLoading,
            isMetricsMetadataLoading,
            dashboard,
            clientId,
            setChartRef,
            clearConfiguredMetric,
            allDashboards,
            t
        } = this.props;
        const {
            addMetricActive,
            metricsMetadata,
            selectedMetricGraph,
            existingPlanGraphs
        } = this.state;
        if (isMetricsMetadataLoading) {
            return (
                <Column>
                    <Loader theme={theme} active />
                </Column>
            );
        }
        return (
            <>
                <Box
                    padding="20px 0px"
                    ref={setChartRef}
                    className={theme.scrollMargin}
                >
                    <Columns alignY="center">
                        <Column width="fluid">
                            <Text fontSize={4} fontWeight="600">
                                {t('pjpAnalytics.insightsCharts')}
                            </Text>
                        </Column>
                        <Column width="content">
                            <PermissionMatch
                                operations={RoleOperationEnum.EDIT_PJP}
                                render={hasPermission => (
                                    <Tooltip
                                        shouldWrapChildren
                                        tooltip={
                                            !hasPermission ? (
                                                <AccessDeniedMessage
                                                    action={t(
                                                        'pjpAnalytics.addNewMetric'
                                                    )}
                                                />
                                            ) : null
                                        }
                                    >
                                        <Button
                                            appearance="solid"
                                            icon="add"
                                            onClick={this.addMetric}
                                            disabled={!hasPermission}
                                            palette="primary"
                                        >
                                            {t('pjpAnalytics.addNewMetric')}
                                        </Button>
                                    </Tooltip>
                                )}
                            />
                        </Column>
                    </Columns>
                </Box>
                <PjpMetricGraphs
                    areDashboardsLoading={areDashboardsLoading}
                    dashboard={dashboard}
                    planId={planId}
                    fetchMetric={this.fetchPjpMetric}
                    pollFetchMetric={this.pollFetchMetric}
                    clientId={clientId}
                    onEditChart={this.onEditChart}
                    t={t}
                    toggleExistingBeat={this.toggleExistingBeat}
                    graphsWithExistingBeat={existingPlanGraphs}
                    onDeleteChart={this.onDeleteChart}
                    allDashboards={allDashboards}
                    fetchPjpChartsData={this.fetchPjpChartsData}
                />
                <OverlayDialog
                    theme={theme}
                    active={this.state.addMetricActive}
                    onEscapeClick={this.onEscapeClick}
                >
                    <EditPjpMetricContainer
                        clearConfiguredMetric={clearConfiguredMetric}
                        editingMetric={
                            addMetricActive ? selectedMetricGraph : null
                        }
                        t={t}
                        isEdit={addMetricActive}
                        fetchPjpChartsData={this.fetchPjpChartsData}
                        planId={planId}
                        fetchPjpMetricData={this.fetchPjpMetric}
                        metricsMetadata={metricsMetadata}
                        theme={theme}
                        onBackClick={this.onEscapeClick}
                    />
                </OverlayDialog>
            </>
        );
    }
}
const mapStateToProps = (state, ownProps: Props) => {
    const isMetricsMetadataLoading = isPjpMetricMetadataLoading(
        state,
        ownProps
    );
    const metricsMetadata = getPjpMetricsMetadata(state, ownProps);
    const areDashboardsLoading = isPjpDashboardsLoading(state, ownProps);
    let allDashboards;
    let dashboardGraphMap = new Map();
    const updatedPjpDashboards = [];
    if (!areDashboardsLoading) {
        allDashboards = getAllPjpInsightsDashboards(state, ownProps);
        if (allDashboards)
            allDashboards.forEach(dashboard => {
                dashboard.dashboardData?.graphs.forEach(graph => {
                    updatedPjpDashboards.push(graph);
                    dashboardGraphMap = dashboardGraphMap.set(
                        graph.id,
                        dashboard.dashboardId
                    );
                });
            });
    }
    const dashboard = {
        dashboardData: {
            graphs: updatedPjpDashboards
        }
    };
    const personnelClientId = personnelClientIdSelector(state);
    const personnelId = personnelIdSelector(state);
    return {
        areDashboardsLoading,
        isMetricsMetadataLoading,
        metricsMetadata,
        dashboard,
        allDashboards,
        personnelClientId,
        personnelId,
        dashboardGraphMap
    };
};
export default connect(mapStateToProps, {
    fetchPjpMetric: PjpInsightsActions.fetchPjpMetric,
    fetchPjpMetricsMetadata: PjpInsightsActions.fetchPjpMetricsMetadata,
    fetchAllDashboards: PjpInsightsActions.fetchAllPjpInsightsDashboards,
    clearConfiguredMetric: PjpInsightsActions.clearConfiguredMetric,
    deleteDashboard: PjpInsightsActions.deletePjpInsightsDashboard
})(PjpChartsContainer);
